import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Tooltip } from '@mui/material'
import { TextField } from '@carsys/form'
import { IShopWholesaler, IUserField } from '@cbgms/api/modules/wholesaler'
import { isMijnGrossierOrMgNl, isVPOMAXBE } from 'utils/Ordersystems/utils'

interface Props {
  wholesaler: IShopWholesaler
  field: IUserField
}

interface IUserFieldTranslations {
  mijngrossier: IMijnGrossierTranslations
  axbe: IAxBeTranslations
}
interface IFieldTranslation {
  name: string
  description: string
}

interface IMijnGrossierTranslations {
  userid: IFieldTranslation
  externalid: IFieldTranslation
}

interface IAxBeTranslations {
  userid: IFieldTranslation
  externalid: IFieldTranslation
}

const userFieldTranslations: IUserFieldTranslations = {
  mijngrossier: {
    userid: {
      name: 'configuration.dynamicproperties.mg.userid',
      description: 'configuration.dynamicproperties.mg.userid_desc'
    },
    externalid: {
      name: 'configuration.dynamicproperties.mg.externalid',
      description: 'configuration.dynamicproperties.mg.externalid_descr'
    }
  },
  axbe: {
    userid: {
      name: 'configuration.dynamicproperties.axbe.userid',
      description: 'configuration.dynamicproperties.axbe.userid_descr'
    },
    externalid: {
      name: 'configuration.dynamicproperties.axbe.externalid',
      description: 'configuration.dynamicproperties.axbe.externalid_descr'
    }
  }
}

const useFieldTranslation = (wholesaler: IShopWholesaler, field: IUserField): IFieldTranslation => {
  const { t, i18n } = useTranslation()
  return useMemo(() => {
    // check if we have specific translations for this ordersystem
    let overwriteTranslation: IFieldTranslation | null = null
    if (
      isMijnGrossierOrMgNl(wholesaler) &&
      userFieldTranslations.mijngrossier[field.Name.toLocaleLowerCase() as keyof IMijnGrossierTranslations]
    ) {
      overwriteTranslation = userFieldTranslations.mijngrossier[
        field.Name.toLocaleLowerCase() as keyof IMijnGrossierTranslations
      ] as IFieldTranslation
    }
    if (isVPOMAXBE(wholesaler) && userFieldTranslations.axbe[field.Name.toLocaleLowerCase() as keyof IAxBeTranslations]) {
      overwriteTranslation = userFieldTranslations.axbe[field.Name.toLocaleLowerCase() as keyof IAxBeTranslations] as IFieldTranslation
    }

    // fallback on default translations
    let name = field.DisplayName
    if (overwriteTranslation && i18n.exists(overwriteTranslation.name)) name = t(overwriteTranslation.name)
    else if (i18n.exists(field.DisplayName)) name = t(field.DisplayName)
    else if (i18n.exists(field.DisplayName.toLocaleLowerCase())) name = t(field.DisplayName.toLocaleLowerCase())

    let description = field.Description
    if (overwriteTranslation && i18n.exists(overwriteTranslation.description)) description = t(overwriteTranslation.description)
    else if (i18n.exists(field.Description)) description = t(field.Description)
    else if (i18n.exists(field.Description.toLocaleLowerCase())) description = t(field.Description.toLocaleLowerCase())

    const result: IFieldTranslation = { name, description }
    return result
  }, [wholesaler, field])
}

export const WholesalerCredentialField: React.FC<Props> = ({ wholesaler, field }) => {
  const { t } = useTranslation()
  const fieldValidation = useCallback((field: IUserField, value: string) => {
    if (field.IsRequired && !value) {
      return t('global.required')
    }
  }, [])

  const translations = useFieldTranslation(wholesaler, field)

  return (
    <Tooltip key={field.Name} title={translations.description} sx={{ width: '100%' }}>
      <Box>
        <TextField
          validate={value => fieldValidation(field, value)}
          disabled={field.Disabled}
          required={field.IsRequired}
          type={field.Name === 'Password' || field.Name === 'CurrentPassword' ? 'password' : 'text'}
          name={`Credentials.${field.Name}`}
          autoComplete='new-password'
          label={translations.name}
          data-testid={field.Name}
        />
      </Box>
    </Tooltip>
  )
}

export default WholesalerCredentialField
